// Castintech dashboard — Issue detail panel (sag taraf overlay).
// Faz 9b PR9 — issue card'a tiklayinca acilan ayrintili panel:
// - openDetail / closeDetail: panel ac/kapat + state.openIssueId
// - dpTab: Bilgi/Yorumlar/Log tab switch
// - loadComments, loadRunLog: comments + heartbeat run log fetch + render
// - patchIssueField: status/priority/assignee inline PATCH
// - updateCommentChars / commentKeydown / submitComment: yorum ekleme
//
// State: state.tasks, state.agents, state.openIssueId, state.patchInFlight, state.commentInFlight.
// Cross-module: toast (toast.js), renderKanban (pano.js — window).
// HTML inline onclick: window.openDetail / closeDetail / dpTab / patchIssueField /
//                      submitComment / commentKeydown / updateCommentChars.

import { state } from '../state.js';
import * as Api from '../api.js';
import { escHtml, ago, normStatus, statusLabel, priorityLabel, getGem, $ } from '../utils.js';
import { toast } from './toast.js';
import { refresh } from '../main.js';
import { renderKanban } from '../views/pano.js';
import type { Issue, Agent } from '../../types/api';

type PatchField = 'status' | 'priority' | 'assigneeId';

export async function openDetail(issueId: string): Promise<void> {
  if (!issueId) return;
  const t = state.tasks.find(x => x.id === issueId);
  if (!t) return;

  state.openIssueId = issueId;
  if (state.currentPage === 'pano') renderKanban();

  const aMap: Record<string, Agent> = {};
  state.agents.forEach(a => { if (a.id) aMap[a.id] = a; });
  const a = aMap[t.assigneeAgentId || t.assignee_id || ''];
  const gem = a ? getGem(a.name) : null;
  const ns = normStatus(t.status);
  const prio = (t.priority || '').toLowerCase();

  const idEl = $('dp-id');
  if (idEl) idEl.textContent = t.identifier || t.id?.substring(0, 8) || '—';
  const titleEl = $('dp-title');
  if (titleEl) titleEl.textContent = t.title || 'Görev';
  const descEl = $('dp-desc');
  if (descEl) descEl.textContent = t.description || 'Açıklama yok.';
  const companyEl = $('dp-company');
  if (companyEl) companyEl.textContent = t.company_name || '—';

  const agentEl = $('dp-agent-info');
  if (agentEl) agentEl.innerHTML = gem
    ? `<span class="${gem.cls}" style="color:var(--gc)">${gem.name}</span> <span style="color:var(--muted)">· ${gem.role || a?.title || ''}</span>`
    : 'Atanmamış';

  const metaEl = $('dp-meta');
  if (metaEl) metaEl.innerHTML = `
    <span class="status-badge sb-${ns}">${statusLabel(t.status)}</span>
    ${prio ? `<span class="priority-badge pb-${prio}">${priorityLabel(t.priority)}</span>` : ''}
    <span style="font-size:11px;color:var(--muted)">${ago(t.updatedAt || t.created_at || '')}</span>`;

  $('detail-panel')?.classList.add('open');
  $('detail-overlay')?.classList.add('visible');

  // Faz 2: PATCH dropdown sync
  const stSel = $<HTMLSelectElement>('dp-edit-status');
  const prSel = $<HTMLSelectElement>('dp-edit-priority');
  const asSel = $<HTMLSelectElement>('dp-edit-assignee');
  if (stSel) stSel.value = ns;
  if (prSel) prSel.value = (t.priority || '').toLowerCase();
  if (asSel) {
    asSel.innerHTML = '<option value="">— Atama yok —</option>'
      + state.agents.slice().sort((x, y) => (x.name || '').localeCompare(y.name || '')).map(ag => {
        const g = getGem(ag.name);
        return `<option value="${ag.id}">${g.icon || '◆'} ${g.name || ag.name}</option>`;
      }).join('');
    asSel.value = t.assigneeAgentId || t.assignee_id || '';
  }

  // Load comments
  dpTab('bilgi');
  // Faz 3: yorum textarea'sını her yeni issue açılışında sıfırla
  const ta = $<HTMLTextAreaElement>('dp-comment-input');
  if (ta) { ta.value = ''; updateCommentChars(); }
  loadComments(t);
  loadRunLog(t);
}

interface Comment {
  authorAgentId?: string;
  authorUserId?: string;
  body?: string;
  createdAt?: string;
}

export async function loadComments(t: Issue): Promise<void> {
  const el = $('dp-comments-list');
  if (!el) return;
  el.innerHTML = '<div class="log-loading">Yorumlar yükleniyor...</div>';
  try {
    const res = await Api.getIssueComments(t.id);
    const data = await res.json();
    const comments: Comment[] = data.comments || data || [];
    if (!comments.length) {
      el.innerHTML = '<div class="empty-state"><div class="empty-icon">💬</div><div class="empty-title">Yorum yok</div></div>';
      return;
    }
    const aMap: Record<string, Agent> = {};
    state.agents.forEach(a => { if (a.id) aMap[a.id] = a; });
    el.innerHTML = comments.map(c => {
      const ca = c.authorAgentId ? aMap[c.authorAgentId] : null;
      const gem = ca ? getGem(ca.name) : null;
      return `
        <div class="comment-item ${gem ? gem.cls : ''}">
          <div class="comment-header">
            <span class="comment-agent">${gem ? gem.name : (c.authorUserId ? 'Kullanıcı' : 'Sistem')}</span>
            <span class="comment-time">${ago(c.createdAt || '')}</span>
          </div>
          <div class="comment-body">${(c.body || '').substring(0, 600)}</div>
        </div>`;
    }).join('');
  } catch {
    el.innerHTML = '<div style="color:var(--muted);padding:20px;font-size:12px">Yorumlar yüklenemedi</div>';
  }
}

interface LogChunkBlock {
  type?: string;
  text?: string;
  name?: string;
  input?: unknown;
  content?: unknown;
}

interface LogChunk {
  type?: string;
  subtype?: string;
  cost_usd?: number;
  message?: { content?: LogChunkBlock[] };
}

export async function loadRunLog(t: Issue): Promise<void> {
  const el = $('dp-log-viewer');
  if (!el) return;
  const runId = t.executionRunId || t.checkoutRunId;
  if (!runId) {
    el.innerHTML = '<div class="log-loading">Bu görev için run kaydı yok</div>';
    return;
  }
  el.innerHTML = '<div class="log-loading">Log yükleniyor...</div>';
  try {
    const res = await Api.getHeartbeatRunLog(runId);
    const data = await res.json();
    const content: string = data.content || '';
    if (!content) { el.innerHTML = '<div class="log-loading">Log boş</div>'; return; }
    let html = '';
    for (const line of content.split('\n')) {
      if (!line.trim()) continue;
      try {
        const entry = JSON.parse(line);
        const chunk: LogChunk = JSON.parse(entry.chunk || '{}');
        if (chunk.type === 'assistant') {
          for (const b of chunk.message?.content || []) {
            if (b.type === 'text') html += `<div class="log-text">${escHtml((b.text || '').substring(0, 400))}</div>`;
            else if (b.type === 'tool_use') html += `<div class="log-tool">▶ ${b.name}(${escHtml(JSON.stringify(b.input || {}).substring(0, 120))})</div>`;
          }
        } else if (chunk.type === 'user') {
          for (const b of chunk.message?.content || []) {
            if (b.type === 'tool_result') html += `<div class="log-result">← ${escHtml(String(b.content || '').substring(0, 200))}</div>`;
          }
        } else if (chunk.type === 'result') {
          html += `<div class="log-done">✓ ${chunk.subtype || 'done'} · $${((chunk.cost_usd || 0)).toFixed(4)}</div>`;
        }
      } catch {}
    }
    el.innerHTML = html || '<div class="log-loading">Log parse edilemedi</div>';
    el.scrollTop = el.scrollHeight;
  } catch {
    el.innerHTML = '<div class="log-loading">Log yüklenemedi</div>';
  }
}

export function closeDetail(): void {
  state.openIssueId = null;
  $('detail-panel')?.classList.remove('open');
  $('detail-overlay')?.classList.remove('visible');
  if (state.currentPage === 'pano') renderKanban();
}

export function dpTab(name: string): void {
  document.querySelectorAll<HTMLElement>('.dp-tab').forEach((t, i) => {
    const names = ['bilgi', 'yorumlar', 'log'];
    t.className = 'dp-tab' + (names[i] === name ? ' active' : '');
  });
  ['bilgi', 'yorumlar', 'log'].forEach(n => {
    const el = $('dpt-' + n);
    if (el) el.className = 'dp-tab-content' + (n === name ? ' active' : '');
  });
}

// ── Faz 2: Issue PATCH (status / priority / assignee inline edit) ──

export async function patchIssueField(field: PatchField, value: string, selectEl: HTMLSelectElement): Promise<void> {
  if (!state.openIssueId || state.patchInFlight) return;
  const t = state.tasks.find(x => x.id === state.openIssueId);
  if (!t) return;
  const oldValue = field === 'assigneeId'
    ? (t.assigneeAgentId || t.assignee_id || '')
    : ((t as Record<string, unknown>)[field] as string || '');
  if ((value || '') === (oldValue || '')) return;

  state.patchInFlight = true;
  selectEl.disabled = true;
  const body: Record<string, string | null> = {};
  body[field] = value || null;
  try {
    const r = await Api.patchIssue(state.openIssueId, body);
    if (!r.ok) {
      const errText = await r.text().catch(() => `HTTP ${r.status}`);
      throw new Error(errText.substring(0, 100) || `HTTP ${r.status}`);
    }
    toast(`${field} güncellendi`, 'success');
    if (field === 'status') t.status = value;
    else if (field === 'priority') t.priority = value;
    else if (field === 'assigneeId') {
      t.assigneeAgentId = value;
      t.assignee_id = value;
    }
    if (state.currentPage === 'pano') renderKanban();
    refresh();
  } catch (e) {
    toast(`Güncelleme başarısız: ${(e as Error).message}`, 'error');
    selectEl.value = oldValue;
  } finally {
    state.patchInFlight = false;
    selectEl.disabled = false;
  }
}

// ── Faz 3: Yorum yazma (comment compose) ─────────────────────────────

export function updateCommentChars(): void {
  const ta = $<HTMLTextAreaElement>('dp-comment-input');
  const el = $('dp-comment-chars');
  if (!ta || !el) return;
  const n = ta.value.length;
  el.textContent = `${n} / 2000`;
  el.classList.toggle('over', n > 2000);
}

export function commentKeydown(e: KeyboardEvent): void {
  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
    e.preventDefault();
    submitComment();
  }
}

export async function submitComment(): Promise<void> {
  if (!state.openIssueId || state.commentInFlight) return;
  const ta = $<HTMLTextAreaElement>('dp-comment-input');
  const body = (ta?.value || '').trim();
  if (!body) { toast('Yorum boş olamaz', 'error'); return; }
  if (body.length > 2000) { toast('Yorum 2000 karakteri geçemez', 'error'); return; }

  const btn = $<HTMLButtonElement>('dp-comment-submit');
  state.commentInFlight = true;
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Gönderiliyor...';
  }

  try {
    const r = await Api.postIssueComment(state.openIssueId, body);
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${r.status}`);
    }
    if (ta) ta.value = '';
    updateCommentChars();
    toast('✓ Yorum eklendi', 'success');
    const t = state.tasks.find(x => x.id === state.openIssueId);
    if (t) await loadComments(t);
  } catch (e) {
    toast('Yorum eklenemedi: ' + (e as Error).message, 'error');
  } finally {
    state.commentInFlight = false;
    if (btn) {
      btn.disabled = false;
      btn.textContent = 'Gönder';
    }
  }
}

// ── Faz 10 PR2 — Detail panel statik click/change bind ──────────────
// HTML inline onclick/onchange/oninput/onkeydown handler'lari silindi;
// main.js bootstrap bu fn'i cagirir.
let _detailStaticBound = false;
export function bindDetailPanelStatic(): void {
  if (_detailStaticBound) return;

  // Overlay click → closeDetail
  const overlay = $('detail-overlay');
  if (overlay) overlay.addEventListener('click', () => closeDetail());

  // Close × button (detail-panel header)
  const panel = $('detail-panel');
  const closeBtn = panel ? panel.querySelector<HTMLElement>('.dp-close') : null;
  if (closeBtn) closeBtn.addEventListener('click', () => closeDetail());

  // Tab buttons — Bilgi / Yorumlar / Log (sirayla 3 button)
  const tabs = panel ? panel.querySelectorAll<HTMLElement>('.dp-tabs .dp-tab') : ([] as unknown as NodeListOf<HTMLElement>);
  const tabNames = ['bilgi', 'yorumlar', 'log'];
  tabs.forEach((btn, i) => {
    const name = tabNames[i];
    if (name) btn.addEventListener('click', () => dpTab(name));
  });

  // PATCH select dropdown'lar (status/priority/assignee)
  const stSel = $<HTMLSelectElement>('dp-edit-status');
  if (stSel) stSel.addEventListener('change', (e) => patchIssueField('status', (e.currentTarget as HTMLSelectElement).value, e.currentTarget as HTMLSelectElement));
  const prSel = $<HTMLSelectElement>('dp-edit-priority');
  if (prSel) prSel.addEventListener('change', (e) => patchIssueField('priority', (e.currentTarget as HTMLSelectElement).value, e.currentTarget as HTMLSelectElement));
  const asSel = $<HTMLSelectElement>('dp-edit-assignee');
  if (asSel) asSel.addEventListener('change', (e) => patchIssueField('assigneeId', (e.currentTarget as HTMLSelectElement).value, e.currentTarget as HTMLSelectElement));

  // Yorum textarea (input + keydown)
  const ta = $<HTMLTextAreaElement>('dp-comment-input');
  if (ta) {
    ta.addEventListener('input', () => updateCommentChars());
    ta.addEventListener('keydown', (e) => commentKeydown(e));
  }

  // Yorum gonder button
  const submitBtn = $<HTMLButtonElement>('dp-comment-submit');
  if (submitBtn) submitBtn.addEventListener('click', () => submitComment());

  _detailStaticBound = true;
}
